import { getFileName } from "@msuite/utils/strings";
import type { DocumentStructureItem, Tables } from "@msuite/utils/types";
import { motion } from "motion/react";
import { type RefObject, useRef } from "react";
import { CloseButton } from "../../button";
import { Divider } from "../../divider";
import { useClickOutside, useDisclosure } from "../../hooks";
import { cn } from "../../utils";
import { useFileExplorerContext } from "./context";
import { FileExplorerPreviewFile } from "./file-explorer-preview-file";
import { FileExplorerPreviewFolder } from "./file-explorer-preview-folder";
import { useFileExplorerIcon } from "./use-file-explorer-icon";

/** Props Interface */
interface FileExplorerPreviewProps<T extends { file: Tables<"files"> }> {
	focusedItem: DocumentStructureItem<T>;
	containerRef: React.RefObject<HTMLDivElement | null>;
}

export function FileExplorerPreview<T extends { file: Tables<"files"> }>({
	focusedItem,
	containerRef,
}: FileExplorerPreviewProps<T>) {
	/** Context */
	const disclosure = useDisclosure();
	const { setFocusedItem } = useFileExplorerContext();
	const ref = useRef(null) as unknown as RefObject<HTMLDivElement>;
	useClickOutside({
		ref,
		handler: () => {
			if (disclosure.isOpen) return;
			setFocusedItem(null);
		},
	});

	/** Props */
	const isFolder = focusedItem.type === "folder";
	const name = isFolder ? focusedItem.name : getFileName(focusedItem.file.fileName);
	const icon = useFileExplorerIcon(focusedItem);

	/** Functions */
	function handleOnClose() {
		setFocusedItem(null);
	}

	/** Render */
	return (
		<motion.div
			ref={ref}
			className={cn(
				"absolute right-0 top-[0.5px] w-1/4 z-[2]",
				"modal-padding",
				"v-stack gap-4",
				"card-bg",
				"rounded-r-md",
				"border-l-[0.5px] border-color",
				"overflow-hidden",
			)}
			style={{
				height: (containerRef.current?.clientHeight || 0) + 0,
			}}
			initial={{ x: "100%" }}
			animate={{ x: 0 }}
			exit={{ x: "100%" }}
			transition={{ ease: "easeInOut", duration: 0.2 }}
		>
			<CloseButton onClose={handleOnClose} />
			<div className="h-stack items-baseline">
				<div className="translate-y-0.5">{icon}</div>
				<h3 className="text-sm font-medium">{name}</h3>
			</div>
			<Divider variant="modal" />
			<div className={cn("-modal-padding overflow-y-auto -m-6 -mt-4 p-6")}>
				{isFolder && <FileExplorerPreviewFolder item={focusedItem} />}
				{!isFolder && (
					<FileExplorerPreviewFile
						item={focusedItem}
						disclosure={disclosure}
					/>
				)}
			</div>
		</motion.div>
	);
}
